import React, { useState } from 'react';
import { X, Pentagon, Ship, Download, ArrowUpRight, ArrowDownLeft, Clock, Zap } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useApp } from '@/context/AppContext';
import { getVesselsInGeofence, getGateEventsByGeofence, getStatusColor } from '@/data/mockData';

export const GeofenceModal = () => {
  const { 
    selectedGeofence, 
    geofenceModalOpen, 
    closeGeofenceModal, 
    toggleGeofenceAutoGate,
    selectVessel 
  } = useApp();

  if (!selectedGeofence) return null;

  const vesselsInGeofence = getVesselsInGeofence(selectedGeofence.id);
  const gateEvents = getGateEventsByGeofence(selectedGeofence.id);

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExportExcel = () => {
    // Create CSV content
    const headers = ['No', 'Nama Kapal', 'MMSI', 'Status', 'SOG'];
    const rows = vesselsInGeofence.map((vessel, idx) => [
      idx + 1,
      vessel.name,
      vessel.mmsi,
      vessel.status,
      vessel.sog
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `geofence_${selectedGeofence.name.replace(/\s+/g, '_')}_vessels.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleVesselClick = (vessel) => {
    closeGeofenceModal();
    setTimeout(() => selectVessel(vessel), 300);
  };

  return (
    <Dialog open={geofenceModalOpen} onOpenChange={(open) => !open && closeGeofenceModal()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-border bg-accent/30">
          <div className="flex items-start gap-4">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${selectedGeofence.color}20` }}
            >
              <Pentagon 
                className="w-6 h-6" 
                style={{ color: selectedGeofence.color }}
              />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold">
                Kapal di dalam Geofence: {selectedGeofence.name}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-1">
                <Badge variant="outline">{selectedGeofence.portAreaType}</Badge>
                <span className="text-xs text-muted-foreground">
                  Created: {formatDateTime(selectedGeofence.createdAt)}
                </span>
              </DialogDescription>
            </div>
          </div>

          {/* Total vessels stat */}
          <div className="mt-4 p-4 bg-card rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="text-3xl font-bold text-primary">{vesselsInGeofence.length}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Total Kapal dalam Area</p>
                  <p className="text-xs text-muted-foreground">Terdeteksi saat ini</p>
                </div>
              </div>
              <Button onClick={handleExportExcel} variant="outline" size="sm" className="gap-2">
                <Download className="w-4 h-4" />
                Export Excel
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="vessels" className="flex-1">
          <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent px-6 h-12">
            <TabsTrigger value="vessels" className="text-xs">Daftar Kapal ({vesselsInGeofence.length})</TabsTrigger>
            <TabsTrigger value="events" className="text-xs">Event Log ({gateEvents.length})</TabsTrigger>
            <TabsTrigger value="settings" className="text-xs">Pengaturan</TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[400px]">
            {/* Vessels Tab */}
            <TabsContent value="vessels" className="m-0 p-0">
              {vesselsInGeofence.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs w-12">No</TableHead>
                      <TableHead className="text-xs">Nama Kapal</TableHead>
                      <TableHead className="text-xs">MMSI</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs text-right">SOG</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vesselsInGeofence.map((vessel, idx) => (
                      <TableRow 
                        key={vessel.id}
                        className="table-row-interactive"
                        onClick={() => handleVesselClick(vessel)}
                      >
                        <TableCell className="text-xs font-medium">{idx + 1}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Ship className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium">{vessel.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs font-mono">{vessel.mmsi}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={cn(
                              "text-xs",
                              vessel.status === 'Underway' && "border-success text-success",
                              vessel.status === 'At Anchor' && "border-primary text-primary",
                              vessel.status === 'Moored' && "border-info text-info"
                            )}
                          >
                            {vessel.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono">{vessel.sog} kn</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Ship className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-sm">Tidak ada kapal dalam area geofence ini</p>
                </div>
              )}
            </TabsContent>

            {/* Events Tab */}
            <TabsContent value="events" className="m-0 p-0">
              {gateEvents.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs">Waktu</TableHead>
                      <TableHead className="text-xs">Kapal</TableHead>
                      <TableHead className="text-xs">Event</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gateEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="text-xs">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3 text-muted-foreground" />
                            {formatDateTime(event.timestamp)}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm font-medium">{event.vesselName}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={cn(
                              "text-xs gap-1",
                              event.eventType === 'enter' ? "border-success text-success" : "border-warning text-warning"
                            )}
                          >
                            {event.eventType === 'enter' ? (
                              <ArrowDownLeft className="w-3 h-3" />
                            ) : (
                              <ArrowUpRight className="w-3 h-3" />
                            )}
                            {event.eventType === 'enter' ? 'Masuk' : 'Keluar'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Clock className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-sm">Belum ada event yang tercatat</p>
                </div>
              )}
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="m-0 p-6 space-y-6">
              {/* Auto Gate Toggle */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-accent/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Auto Gate (Masuk/Keluar Otomatis)</Label>
                      <p className="text-xs text-muted-foreground">
                        Deteksi otomatis kapal masuk dan keluar geofence
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={selectedGeofence.autoGate}
                    onCheckedChange={() => toggleGeofenceAutoGate(selectedGeofence.id)}
                  />
                </div>

                {selectedGeofence.autoGate && (
                  <div className="p-4 bg-muted/50 rounded-lg border border-dashed border-border">
                    <p className="text-xs text-muted-foreground">
                      <strong className="text-foreground">Note:</strong> Fitur Auto Gate akan terintegrasi dengan 
                      Pelindo pilotage app / gate system untuk pencatatan otomatis kedatangan dan keberangkatan kapal.
                    </p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Geofence Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Informasi Geofence</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="text-sm font-medium">{selectedGeofence.type}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Area Type</p>
                    <p className="text-sm font-medium">{selectedGeofence.portAreaType}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg col-span-2">
                    <p className="text-xs text-muted-foreground">Polygon Points</p>
                    <p className="text-sm font-medium">{selectedGeofence.polygonCoords.length} coordinates</p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default GeofenceModal;
